import type { AnimalItem } from "../../../../../mocks/animals";
import {
  getAnimalSalesType,
  getDisplayKupeNo,
  getDisplaySiparisNo,
} from "../../../../../lib/animalLabels";

interface Props {
  animal: AnimalItem;
}

const durumStyle: Record<string, string> = {
  Aktif: "bg-farm-green-pale text-farm-green",
  "Kesim Bekliyor": "bg-farm-gold-pale text-farm-gold",
  Kesildi: "bg-farm-earth-pale text-farm-earth",
  "Teslim Edildi": "bg-farm-cream text-farm-muted border border-farm-border",
};

const fmt = (n: number) =>
  n.toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });

export default function AnimalSummary({ animal }: Props) {
  const eksikHisse = Math.max(0, animal.toplamHisse - animal.satirlanHisse);

  const toplamSatis = animal.hisseler.reduce((s, h) => s + h.tutar, 0);
  const toplamHizmet = animal.hisseler.reduce(
    (s, h) =>
      s +
      (h.kesimUcreti ?? 0) +
      (h.parcalamaUcreti ?? 0) +
      (h.tasimaUcreti ?? 0) -
      (h.indirim ?? 0),
    0
  );
  const toplamTahsilat = toplamSatis + toplamHizmet;
  const toplamOdenen = animal.hisseler.reduce((s, h) => s + h.odenen, 0);
  const toplamKalan = animal.hisseler.reduce((s, h) => s + h.kalan, 0);

  const displayKupeNo = getDisplayKupeNo(animal);
  const displaySiparisNo = getDisplaySiparisNo(animal);
  const satisTipi = getAnimalSalesType(animal);

  return (
    <div className="bg-farm-warm border border-farm-border rounded-farm overflow-hidden mb-4">
      <div className="flex items-center justify-between px-4 py-3 border-b border-farm-border bg-farm-cream">
        <div className="flex items-center gap-2.5 flex-wrap">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-farm-earth-pale text-farm-earth">
            <i className="ri-funds-box-line text-sm" />
          </div>

          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-heading font-bold text-sm text-farm-brown">
                {displayKupeNo}
              </span>

              <span
                className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                  animal.tur === "Büyükbaş"
                    ? "bg-farm-earth-pale text-farm-earth"
                    : "bg-farm-green-pale text-farm-green"
                }`}
              >
                {animal.tur}
              </span>

              <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-white text-farm-brown border border-farm-border">
                {satisTipi}
              </span>

              {animal.hisseliSatisEtiketi && (
                <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-white text-farm-brown border border-farm-border">
                  Hisseli Satış
                </span>
              )}

              <span
                className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${
                  durumStyle[animal.durum]
                }`}
              >
                {animal.durum}
              </span>
            </div>

            <p className="font-body text-[10px] text-farm-muted mt-0.5">
              Sipariş No: {displaySiparisNo}
            </p>
          </div>
        </div>
      </div>

      <div className="px-4 py-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-2 mb-3">
          {[
            { label: "Ağırlık", value: animal.kg ? `${animal.kg} kg` : "—" },
            {
              label: "Referans Fiyat",
              value: animal.referansFiyat ? `${animal.referansFiyat} ₺/kg` : "—",
            },
            {
              label: "Tahmini Değer",
              value:
                animal.kg && animal.referansFiyat
                  ? fmt(animal.kg * animal.referansFiyat)
                  : "—",
            },
            { label: "Toplam Hisse", value: `${animal.toplamHisse} hisse` },
            {
              label: "Kesim Günü",
              value: animal.kesimGunu || "—",
            },
            {
              label: "Hisseli Satış",
              value: animal.hisseliSatisEtiketi ? "Evet" : "Hayır",
            },
            {
              label: "Satış Tipi",
              value: satisTipi,
            },
            {
              label: "Küpe / Sipariş",
              value: `${displayKupeNo} / ${displaySiparisNo}`,
            },
          ].map((item) => (
            <div key={item.label} className="bg-farm-cream rounded px-2.5 py-2">
              <p className="font-body text-[10px] text-farm-muted mb-0.5">
                {item.label}
              </p>
              <p className="font-body text-xs font-semibold text-farm-brown">
                {item.value}
              </p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="bg-farm-green-pale/60 rounded px-2.5 py-2 text-center">
            <p className="font-body text-[10px] text-farm-muted mb-0.5">Satılan</p>
            <p className="font-heading font-bold text-sm text-farm-green">
              {animal.satirlanHisse}
            </p>
          </div>

          <div
            className={`rounded px-2.5 py-2 text-center ${
              eksikHisse > 0 ? "bg-farm-earth-pale/60" : "bg-farm-cream"
            }`}
          >
            <p className="font-body text-[10px] text-farm-muted mb-0.5">Boş</p>
            <p
              className={`font-heading font-bold text-sm ${
                eksikHisse > 0 ? "text-farm-earth" : "text-farm-muted"
              }`}
            >
              {eksikHisse}
            </p>
          </div>

          <div className="bg-farm-cream rounded px-2.5 py-2 text-center">
            <p className="font-body text-[10px] text-farm-muted mb-0.5">Toplam</p>
            <p className="font-heading font-bold text-sm text-farm-brown">
              {animal.toplamHisse}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div className="bg-farm-cream rounded px-2.5 py-2">
            <p className="font-body text-[10px] text-farm-muted mb-0.5">Et Satışı</p>
            <p className="font-body text-xs font-semibold text-farm-brown">
              {fmt(toplamSatis)}
            </p>
          </div>

          <div className="bg-farm-cream rounded px-2.5 py-2">
            <p className="font-body text-[10px] text-farm-muted mb-0.5">
              Hizmet Ücreti
            </p>
            <p className="font-body text-xs font-semibold text-farm-brown">
              {fmt(toplamHizmet)}
            </p>
          </div>

          <div className="bg-farm-gold-pale/60 rounded px-2.5 py-2">
            <p className="font-body text-[10px] text-farm-muted mb-0.5">
              Toplam Tahsilat
            </p>
            <p className="font-body text-xs font-bold text-farm-gold">
              {fmt(toplamTahsilat)}
            </p>
          </div>

          <div className="bg-farm-green-pale/60 rounded px-2.5 py-2">
            <p className="font-body text-[10px] text-farm-muted mb-0.5">
              Toplam Ödenen
            </p>
            <p className="font-body text-xs font-bold text-farm-green">
              {fmt(toplamOdenen)}
            </p>
          </div>
        </div>

        {toplamKalan > 0 && (
          <div className="mt-2 flex items-center justify-between px-3 py-2 rounded bg-farm-earth-pale/50 border border-farm-earth/15">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center text-farm-earth">
                <i className="ri-time-line text-sm" />
              </div>
              <span className="font-body text-xs text-farm-muted">Toplam Kalan</span>
            </div>
            <span className="font-body text-sm font-bold text-farm-earth">
              {fmt(toplamKalan)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}